import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {merge} from '#core/utils/config-utils.js';
import {isMobile} from '#core/utils/context-utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';
import {Intents} from '#core/application/intents/index.js';

import {generatePivotButton} from '#core/application/modules/tiles/pivot/index.js';
import {generatePopover, generateSimplePopover} from '#core/application/modules/tiles/popover/index.js';
import {generateSettingMenuGlobalStuff} from '#core/application/modules/tiles/settings/index.js';
import {
    generateRecentSearch,
    generateSearchResultGroup,
    generateSearchResultItem,
} from '#core/application/modules/tiles/search/search-result.js';
import {generateSearchInput} from '#core/application/modules/tiles/search/search-input.js';
import {generateToggleButtonRow} from '#core/application/modules/tiles/button/toggle-button/index.js';

export function mainNavigationBarClassicFactory(PALETTE) {
    return {
        ApplicationHeader: {
            normalLayout: {
                mainNavigationBar: {
                    rightContainer: {
                        style: {
                            alignItems: 'center',
                        },
                    },
                    searchControllerView: {
                        searchView: {
                            searchResultsPopup: {
                                container: {
                                    style: {
                                        right: 0,
                                    },
                                },
                            },
                        },
                    },
                },
            },
            compactLayout: {
                mainNavigationBar: {
                    searchControllerView: {
                        searchView: {
                            searchResultsPopup: {
                                container: {
                                    style: {
                                        left: 16,
                                    },
                                },
                            },
                        },
                    },
                },
            },
        },
        ApplicationHeaderSkeleton: {
            navigationBarContainer: {
                style: {
                    height: '40px',
                    ...resolveBackground(PALETTE.MAIN_NAV_BAR_CLASSIC_BG),
                },
            },
        },
        MainNavigationBarClassic: {
            style: {
                rootContainer: {
                    ...resolveBackground(PALETTE.MAIN_NAV_BAR_CLASSIC_BG),
                    padding: '0 8px',
                },
            },
            linkButtons: {
                disableSelectedButton: !ApplicationConfig.isEmbedded,
                container: {
                    style: {
                        justifyContent: 'flex-start',
                    },
                },
                selectableButton: generatePivotButton(PALETTE.MAIN_NAV_BAR_PAGE_SELECTOR),
            },
            helpButton: {
                style: {
                    base: {
                        flexDirection: 'column',
                        flexBasis: 40,
                    },
                },
            },
            helpIcon: {
                style: {
                    fill: PALETTE.MAIN_NAV_BAR_HELP_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                    width: 16,
                    height: 16,
                    flexGrow: 0,
                },
            },
            showHelpButton: !!Intents.HELP,
            // Settings icon config is located in SettingsMenuClassic component
            linkTextContainer: {
                style: {
                    flexBasis: '48px',
                },
            },
            // override default values from "main-navigation-bar.yaml"
            searchControllerView: {
                searchView: {
                    searchInput: merge(generateSearchInput(PALETTE.MAIN_NAV_BAR_SEARCH_INPUT), {
                        container: {
                            inactive: {
                                style: {
                                    maxWidth: 'initial',
                                },
                            },
                            active: {
                                style: {
                                    minWidth: 'initial',
                                },
                            },
                        },
                        style: {
                            wrapperContainer: {
                                margin: 0,
                            },
                        },
                    }),
                    messageText: {
                        style: {
                            wordBreak: 'break-word',
                            ...PALETTE.SEARCH_RESULT.SEARCH_RECENT_ITEM_TEXT,
                        },
                    },
                    searchResultsPopup: generatePopover(PALETTE.SEARCH_POPOVER),
                    sportMenu: generateToggleButtonRow(PALETTE.SEARCH_SPORT_MENU_SWITCHER, true),
                },
                searchDiscoveryView: {
                    launcherIcon: icon(PALETTE.MAIN_NAV_BAR_DISCOVERY_SEARCH_LAUNCHER_ICON_COLOR),
                },
            },
            sessionTime: {
                container: {
                    style: {
                        padding: '0 8px',
                    },
                },
                timerVertical: {
                    startTimeLabel: {
                        container: {
                            padding: 0,
                        },
                        style: {
                            ...PALETTE.MAIN_NAV_BAR_SESSION_TIME_LABEL_TEXT,
                        },
                    },
                    timeString: {
                        style: {
                            ...PALETTE.MAIN_NAV_BAR_SESSION_TIME_VALUE_TEXT,
                        },
                    },
                },
            },
            currentTime: {
                container: {
                    style: {
                        flexDirection: 'row',
                    },
                },
                time: {
                    timeBox: {
                        style: {
                            fontSize: 11,
                            ...PALETTE.MAIN_NAV_BAR_CURRENT_TIME_TEXT,
                        },
                    },
                },
                offset: {
                    container: {
                        padding: '0 0 0 4px',
                    },
                    style: {
                        fontSize: 11,
                        ...PALETTE.MAIN_NAV_BAR_CURRENT_TIME_OFFSET_TEXT,
                    },
                },
            },
        },
        ...getSettingsPopupConfig(PALETTE.MAIN_NAV_BAR_SETTINGS),
        ...(!isMobile()
            ? {
                  RecentSearch: generateRecentSearch(PALETTE.SEARCH_RESULT),
                  SearchResultGroup: generateSearchResultGroup(PALETTE.SEARCH_RESULT),
                  SearchResultItem: generateSearchResultItem(PALETTE.SEARCH_RESULT),
              }
            : {}),
    };
}

function getSettingsPopupConfig(PALETTE) {
    return {
        // It exists only in desktop and in header
        SettingsMenuPopup: {
            icon: {
                style: {
                    minWidth: '16px',
                    minHeight: '16px',
                    alignSelf: 'center',
                },
            },
            button: {
                style: {
                    base: {
                        padding: '12px 0px',
                        fontSize: '14px',
                    },
                },
            },
            iconColor: {
                closed: PALETTE.HEADER_SETTINGS_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                open: PALETTE.HEADER_SETTINGS_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
            },
            popup: merge(
                generateSimplePopover(
                    PALETTE.HEADER_SETTINGS_POPOVER_BG,
                    PALETTE.HEADER_SETTINGS_POPOVER_BORDER_RADIUS,
                    PALETTE.HEADER_SETTINGS_POPOVER_SHADOW,
                    PALETTE.HEADER_SETTINGS_POPOVER_BORDER_COLOR
                ),
                {
                    arrowheadOffset: '-8px',
                    container: {
                        style: {
                            width: '272px',
                            top: '40px',
                            right: '6px',
                        },
                    },
                }
            ),
        },

        // Made it global as it is no possibility to pass it to SettingsMenuPopup
        // Anyway for desktop it used only once - in header
        ...(!isMobile() ? generateSettingMenuGlobalStuff(PALETTE.HEADER_SETTINGS) : {}),
    };
}
